import { ArrowLeft, Check, Flag, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../../lib/api.ts';
import type { RunItem } from '../../lib/planning.ts';
import { getCurrentRun, validerEtapeParcours } from '../../lib/planning.ts';
import type { RoundSummary } from '../../lib/rounds.ts';
import { listRounds } from '../../lib/rounds.ts';

const jour = (iso: string): string =>
    new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' });

/* L'étape qui se juge sur une carte, pas sur un seau de balles.
 *
 * ── Pourquoi elle a son écran ───────────────────────────────────────────────
 *
 * Les huit autres ateliers se notent balle par balle au practice. Celui-ci
 * demande trois trous sur un vrai parcours, et se juge à la moyenne au par.
 * Le lancer comme une séance de balles ouvrirait un pavé de saisie que rien ne
 * viendrait jamais remplir.
 *
 * ── Rien ne se déclare ──────────────────────────────────────────────────────
 *
 * On ne coche pas « validé ». On DÉSIGNE un tour déjà joué, et le serveur
 * calcule : les meilleurs trous, leur moyenne au par, le verdict. Un examen
 * qu'on s'auto-attribue n'est pas un examen, et le calcul fait ici, sur le
 * téléphone, reviendrait au même — il suffirait d'envoyer le résultat voulu.
 *
 * ── Les meilleurs trous, et non les premiers ────────────────────────────────
 *
 * La règle fédérale retient les meilleurs. Ce n'est pas une sévérité mais son
 * contraire : sur neuf trous joués, trois ratés ne condamnent pas le passage.
 * L'écran le dit, parce qu'un joueur qui l'ignore renonce en voyant son
 * premier double bogey.
 */
export function EtapeParcours() {
    const { position } = useParams<{ position: string }>();
    const navigate = useNavigate();
    const [item, setItem] = useState<RunItem | null | undefined>(undefined);
    const [runId, setRunId] = useState<string | null>(null);
    const [tours, setTours] = useState<RoundSummary[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        void (async () => {
            try {
                const [run, liste] = await Promise.all([getCurrentRun(), listRounds()]);
                const rang = Number(position);
                setRunId(run.run?.id ?? null);
                setItem(run.items.find((i) => i.position === rang) ?? null);
                // Seuls les tours TERMINÉS sont proposés : une carte en cours
                // peut encore changer, et le résultat serait figé sur un état
                // provisoire.
                setTours(liste.filter((t) => t.status === 'finished'));
            } catch (e) {
                setError(e instanceof ApiError ? e.message : 'Chargement impossible');
                setItem(null);
            }
        })();
    }, [position]);

    if (item === undefined) {
        return (
            <div className="flex justify-center py-16">
                <span className="loading loading-ring loading-lg text-primary" />
            </div>
        );
    }

    const valider = async (roundId: string): Promise<void> => {
        if (!runId || !item) return;
        setBusy(true); setError(null);
        try {
            await validerEtapeParcours(runId, item.position, roundId);
            void navigate('/entrainement/seance-en-cours');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Validation impossible');
            setBusy(false);
        }
    };

    const fait = item?.coursePassed != null;

    return (
        <div className="space-y-4 py-2">
            <Link to="/entrainement/seance-en-cours" className="btn btn-ghost btn-sm gap-2">
                <ArrowLeft size={16} /> Ma séance
            </Link>

            <header>
                <h1 className="font-display text-2xl font-bold tracking-tight">
                    {item?.exerciseName ?? 'Validation du parcours'}
                </h1>
                {item?.criteria && (
                    <p className="mt-2 rounded-box border border-base-300 bg-base-100 px-3 py-2 text-sm italic shadow-sm">
                        « {item.criteria} »
                    </p>
                )}
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            {fait && (
                /* Le résultat déjà constaté. Il ne bouge plus, même si la carte
                   est corrigée après coup : un examen passé garde ce qui a été
                   constaté le jour même. */
                <section className="rounded-box p-4 text-center"
                    style={item!.coursePassed
                        ? { backgroundColor: 'var(--color-primary)', color: 'var(--color-primary-content)' }
                        : { backgroundColor: 'var(--brique)', color: 'var(--brique-encre)' }}>
                    {item!.coursePassed
                        ? <Check size={32} className="mx-auto" aria-hidden="true" />
                        : <X size={32} className="mx-auto" aria-hidden="true" />}
                    <p className="font-display mt-2 text-xl font-bold">
                        {item!.coursePassed ? 'Étape validée' : 'Étape non validée'}
                    </p>
                    <p className="mt-1 text-sm opacity-90">
                        <span className="font-score tabular-nums">
                            {item!.courseAvgOverPar! > 0 ? '+' : ''}
                            {item!.courseAvgOverPar!.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
                        </span>
                        {' au par en moyenne, sur les '}
                        <span className="font-score tabular-nums">{item!.courseHolesCounted}</span>
                        {' meilleurs trous'}
                        {item!.courseMaxAvgOverPar != null
                            && ` · seuil : +${item!.courseMaxAvgOverPar}`}
                    </p>
                </section>
            )}

            <section className="rounded-box bg-base-200 p-4">
                <h2 className="text-sm font-semibold">
                    {fait ? 'Choisir un autre tour' : 'Sur quel tour ?'}
                </h2>
                <p className="mt-1 text-xs opacity-60">
                    {/* Dire la règle de sélection : sans elle, un joueur qui voit
                        son premier double bogey croit l'étape perdue et arrête. */}
                    Seuls les tours terminés apparaissent. Le calcul retient vos{' '}
                    <span className="font-semibold">{item?.courseBestHoles ?? 3} meilleurs trous</span>,
                    pas les premiers.
                </p>

                {tours.length === 0 ? (
                    <div className="mt-3 rounded-field border border-dashed border-base-300 p-6 text-center">
                        <Flag size={26} className="mx-auto opacity-40" aria-hidden="true" />
                        <p className="mt-2 text-sm opacity-70">
                            Aucun tour terminé. Jouez au moins{' '}
                            {item?.courseMinHoles ?? 3} trous, rendez votre carte, puis revenez ici.
                        </p>
                        <Link to="/parcours" className="btn btn-primary mt-3 gap-2">
                            <Flag size={16} /> Aller au parcours
                        </Link>
                    </div>
                ) : (
                    <ul className="mt-3 space-y-2">
                        {tours.map((t) => (
                            <li key={t.id}>
                                <button type="button" disabled={busy}
                                    onClick={() => void valider(t.id)}
                                    className="flex w-full items-center gap-3 rounded-field bg-base-100 p-3 text-left
                                               transition-colors hover:bg-base-300 disabled:opacity-50">
                                    <Flag size={18} className="shrink-0 opacity-50" aria-hidden="true" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate font-semibold">{t.courseName}</span>
                                        <span className="block text-xs opacity-60">
                                            {jour(t.startedAt)} · {t.holesPlayed} trou
                                            {t.holesPlayed > 1 ? 's' : ''} noté{t.holesPlayed > 1 ? 's' : ''}
                                        </span>
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
